import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';
import { createTheme, Theme, ThemeProvider } from '@mui/material/styles';
import Alert from '@mui/material/Alert';
import Snackbar from '@mui/material/Snackbar';
import { useConfiguration } from '../hooks/useConfiguration';
import { a3Theme } from '../config/theme';

interface DynamicThemeContextType {
  theme: Theme;
}

const DynamicThemeContext = createContext<DynamicThemeContextType>({
  theme: a3Theme
});

export const useDynamicTheme = () => useContext(DynamicThemeContext);

interface DynamicThemeProviderProps {
  children: React.ReactNode;
}

export const DynamicThemeProvider: React.FC<DynamicThemeProviderProps> = ({ children }) => {
  const { config, usingDefaults } = useConfiguration();
  const [fallbackAlertOpen, setFallbackAlertOpen] = useState(false);

  useEffect(() => {
    if (usingDefaults) setFallbackAlertOpen(true);
  }, [usingDefaults]);

  const dynamicTheme = useMemo(() => {
    console.log('🎨 Creating dynamic theme with config:', config?.ui);
    
    if (!config?.ui) {
      console.log('🎨 Using default theme (no UI config)');
      return a3Theme;
    }

    // Create theme with dynamic colors from configuration
    const theme = createTheme({
      ...a3Theme,
      palette: {
        ...a3Theme.palette,
        primary: {
          ...a3Theme.palette.primary,
          main: config.ui.primaryColor || a3Theme.palette.primary.main,
        },
        secondary: {
          ...a3Theme.palette.secondary,
          main: config.ui.secondaryColor || a3Theme.palette.secondary.main,
        },
      },
    });
    
    console.log('🎨 Dynamic theme created with colors:', {
      primary: theme.palette.primary.main,
      secondary: theme.palette.secondary.main
    });
    
    return theme;
  }, [config?.ui?.primaryColor, config?.ui?.secondaryColor]);

  const contextValue = useMemo(() => ({
    theme: dynamicTheme
  }), [dynamicTheme]);

  return (
    <DynamicThemeContext.Provider value={contextValue}>
      <ThemeProvider theme={dynamicTheme}>
        {children}
        <Snackbar
          open={fallbackAlertOpen}
          autoHideDuration={6000}
          onClose={() => setFallbackAlertOpen(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setFallbackAlertOpen(false)}
            severity="warning"
            variant="filled"
            sx={{ width: '100%' }}
          >
            Could not load configuration — using defaults.
          </Alert>
        </Snackbar>
      </ThemeProvider>
    </DynamicThemeContext.Provider>
  );
};